import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../lib/api';
import { formatDate, formatRs, rupeesInWords } from '../lib/money';
import { openPrint } from '../lib/print';
import InkPad, { type InkPadHandle } from '../components/InkPad';
import SignatureImage from '../components/SignatureImage';
import LoanReceipt from '../print/LoanReceipt';
import { rosterApi, type WorkerRow } from '../lib/roster';
import {
  loanApi, loanState, perPeriod, periods, weeksToRepay, type LoanRepayment, type LoanRow,
} from '../lib/loans';

/**
 * Loans — the third segment of Pay run (migration 0009; asked for by the owner
 * on 25 September 2026). Not in the prototype, so the wording here is new.
 *
 * The clerk gives a loan and it waits for the owner; the owner's own loans are
 * approved as he gives them. Every pay run the man appears in takes the weekly
 * amount off his pay — that happens on the Worker, never here. This screen
 * gives, re-paces, approves, voids, and shows each loan's repayment record.
 *
 * Requested → approved → issued (25 September 2026). The clerk's loan is a
 * request: it goes to the owner's Approvals. Once approved it shows here as
 * "Approved — hand over the cash"; the clerk taps Issue money, hands it over,
 * and he signs on her tablet — that signature issues the loan, and only an
 * issued loan repays. The owner's own loan is approved at once and he may
 * issue it on the spot or leave it for the clerk. The receipt prints two
 * copies to an A4 once it is issued.
 */

interface Props {
  isOwner: boolean;
  /** The business date, from the Worker — never the tablet's clock. */
  today: string;
  /** Tells the segment control how many loans wait for the owner. */
  onWaiting?: (count: number) => void;
}

type Filter = 'owing' | 'paid' | 'voided';

const STATE_PILL: Record<ReturnType<typeof loanState>, { label: string; cls: string }> = {
  waiting: { label: 'Waiting for approval', cls: 'p-wait' },
  toIssue: { label: 'Approved — hand over the cash', cls: 'p-ok' },
  repaying: { label: 'Repaying', cls: 'p-info' },
  paid: { label: 'Paid off', cls: 'p-ok' },
  voided: { label: 'Voided', cls: 'p-no' },
};

export default function Loans({ isOwner, onWaiting }: Props) {
  const [loans, setLoans] = useState<LoanRow[] | null>(null);
  const [workers, setWorkers] = useState<WorkerRow[]>([]);
  const [filter, setFilter] = useState<Filter>('owing');
  const [open, setOpen] = useState<string | null>(null);
  const [signing, setSigning] = useState<LoanRow | null>(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const result = await loanApi.list();
      setLoans(result.loans);
      onWaiting?.(result.loans.filter((loan) => loanState(loan) === 'waiting').length);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the loans.');
    }
  }, [onWaiting]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    rosterApi.listWorkers()
      .then((result) => setWorkers(result.workers.filter((worker) => worker.isActive)))
      .catch(() => { /* the form says so when there is nobody to pick */ });
  }, []);

  const shown = (loans ?? []).filter((loan) => {
    const state = loanState(loan);
    if (filter === 'owing') return state === 'waiting' || state === 'toIssue' || state === 'repaying';
    if (filter === 'paid') return state === 'paid';
    return state === 'voided';
  });
  const waiting = (loans ?? []).filter((loan) => loanState(loan) === 'waiting');
  const toIssue = (loans ?? []).filter((loan) => loanState(loan) === 'toIssue');
  const owed = (loans ?? [])
    .filter((loan) => loanState(loan) === 'repaying')
    .reduce((sum, loan) => sum + loan.balance_rs, 0);

  return (
    <>
      <GiveLoan
        isOwner={isOwner}
        workers={workers}
        onGiven={async (loan) => {
          setFilter('owing');
          await load();
          setOpen(loan.id);
          if (isOwner) {
            // Approved as he gives it: he may hand the cash over now.
            setSigning(loan);
            setNotice('');
          } else {
            setNotice(`Loan for ${loan.worker_name} sent to the owner for approval. Once he approves it, hand over the cash here and have him sign.`);
          }
        }}
      />
      {signing && (
        <SignLoan
          loan={signing}
          onClose={() => setSigning(null)}
          onSaved={async (loan) => { setSigning(loan); await load(); }}
        />
      )}

      {notice && <div className="banner b-info">{notice}</div>}

      {isOwner && waiting.length > 0 && (
        <div className="banner b-warn">
          {waiting.length} loan{waiting.length > 1 ? 's' : ''} waiting for your approval — they are
          in Approvals too. Nothing is handed over until you approve.
        </div>
      )}

      {toIssue.length > 0 && (
        <div className="banner b-ok">
          {toIssue.length} approved loan{toIssue.length > 1 ? 's' : ''} to hand over:{' '}
          {toIssue.map((loan) => loan.worker_name).join(', ')}. Open it, tap Issue money, and have
          him sign as he takes the cash.
        </div>
      )}

      <div className="card">
        <h3>Loans</h3>
        <div className="sub">
          The repayment comes off his pay until the loan is paid off: every pay week for
          weekly men (less in a short week, by the days worked out of six), once a month on
          pay day for EPF/ETF members.
          {owed > 0 && <> Still owed on issued loans: <b>{formatRs(owed)}</b>.</>}
        </div>

        <div className="seg wide" style={{ margin: '10px 0 4px' }}>
          {(['owing', 'paid', 'voided'] as const).map((key) => (
            <button
              type="button"
              key={key}
              className={filter === key ? 'on' : ''}
              onClick={() => setFilter(key)}
            >
              {key === 'owing' ? 'Owing' : key === 'paid' ? 'Paid off' : 'Voided'}
            </button>
          ))}
        </div>

        {error && <div className="banner b-no">{error}</div>}
        {!loans && !error && <div className="empty">Loading…</div>}
        {loans && shown.length === 0 && (
          <div className="empty">
            {filter === 'owing'
              ? 'Nobody owes anything on a loan.'
              : filter === 'paid'
                ? 'No loan has been paid off yet.'
                : 'No loans have been voided.'}
          </div>
        )}

        {shown.map((loan) => (
          <LoanItem
            key={loan.id}
            loan={loan}
            isOwner={isOwner}
            open={open === loan.id}
            onOpen={() => setOpen(open === loan.id ? null : loan.id)}
            onChanged={load}
            onSign={() => setSigning(loan)}
          />
        ))}
      </div>
    </>
  );
}

// ------------------------------------------------------------- give a loan

function GiveLoan({
  isOwner, workers, onGiven,
}: {
  isOwner: boolean;
  workers: WorkerRow[];
  onGiven: (loan: LoanRow) => Promise<void>;
}) {
  const [workerId, setWorkerId] = useState('');
  const [amount, setAmount] = useState('');
  const [weekly, setWeekly] = useState('');
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const amountRs = Math.trunc(Number(amount) || 0);
  const weeklyRs = Math.trunc(Number(weekly) || 0);
  // An EPF/ETF member is paid monthly, so he repays once a month.
  const cycle: 'weekly' | 'monthly' = workers.find((w) => w.id === workerId)?.epf ? 'monthly' : 'weekly';
  const missing = {
    worker: !workerId,
    amount: amountRs <= 0,
    weekly: weeklyRs <= 0 || weeklyRs > amountRs,
    reason: reason.trim() === '',
  };
  const ok = !missing.worker && !missing.amount && !missing.weekly && !missing.reason;
  const red = (bad: boolean) => (tried && bad ? { borderColor: 'var(--danger)' } : undefined);

  async function give() {
    setTried(true);
    if (!ok) return;
    setBusy(true);
    setError('');
    try {
      const { loan } = await loanApi.give({ workerId, amountRs, weeklyRs, reason: reason.trim() });
      setWorkerId(''); setAmount(''); setWeekly(''); setReason(''); setTried(false);
      await onGiven(loan);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record that loan.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h3>{isOwner ? 'Give a loan' : 'Request a loan'}</h3>
      <div className="sub">
        {isOwner
          ? 'Approved as you give it. Hand over the cash and have him sign — now, or the office can do it later.'
          : 'It goes to the owner for approval. Once he approves, hand over the cash here and have him sign.'}
      </div>

      <div className="fld" style={{ marginTop: 8 }}>
        <div>Employee</div>
        <select value={workerId} onChange={(e) => setWorkerId(e.target.value)} style={{ width: 220, ...red(missing.worker) }}>
          <option value="">Choose…</option>
          {workers.map((worker) => (
            <option key={worker.id} value={worker.id}>{worker.name}</option>
          ))}
        </select>
      </div>
      <div className="fld">
        <div>Loan amount</div>
        <input
          type="number" min="1" step="1" inputMode="numeric" placeholder="Rs"
          value={amount} onChange={(e) => setAmount(e.target.value)}
          style={{ width: 140, ...red(missing.amount) }}
        />
      </div>
      <div className="fld">
        <div>
          {cycle === 'monthly' ? 'Monthly repayment' : 'Weekly repayment'}
          <div className="sub">
            {tried && weeklyRs > amountRs && amountRs > 0
              ? <span style={{ color: 'var(--text-danger)' }}>More than the loan itself</span>
              : amountRs > 0 && weeklyRs > 0 && weeklyRs <= amountRs
                ? `About ${weeksToRepay(amountRs, weeklyRs)} ${cycle === 'monthly' ? 'months' : 'full weeks'} to repay`
                : cycle === 'monthly'
                  ? 'An EPF/ETF member: taken once a month, on pay day'
                  : 'For a full week of six days — a short week repays its share'}
          </div>
        </div>
        <input
          type="number" min="1" step="1" inputMode="numeric" placeholder={cycle === 'monthly' ? 'Rs a month' : 'Rs a week'}
          value={weekly} onChange={(e) => setWeekly(e.target.value)}
          style={{ width: 140, ...red(missing.weekly) }}
        />
      </div>
      <div className="fld">
        <div>
          Reason
          {tried && missing.reason && (
            <div className="sub" style={{ color: 'var(--text-danger)' }}>Write what the loan is for</div>
          )}
        </div>
        <input
          type="text" placeholder="Roof repair, school fees…"
          value={reason} onChange={(e) => setReason(e.target.value)}
          style={{ width: 240, textAlign: 'left', ...red(missing.reason) }}
        />
      </div>

      {error && <div className="banner b-no">{error}</div>}

      <div className="bar" style={{ marginTop: 12 }}>
        <button type="button" className="btn pri" disabled={busy} onClick={() => void give()}>
          {isOwner
            ? amountRs > 0 ? `Give loan of ${formatRs(amountRs)}` : 'Give loan'
            : 'Send for approval'}
        </button>
      </div>
    </div>
  );
}

// -------------------------------------------------------------- one loan

function LoanItem({
  loan, isOwner, open, onOpen, onChanged, onSign,
}: {
  loan: LoanRow;
  isOwner: boolean;
  open: boolean;
  onOpen: () => void;
  onChanged: () => Promise<void>;
  onSign: () => void;
}) {
  const state = loanState(loan);
  const pill = STATE_PILL[state];

  return (
    <>
      <div
        className="row"
        role="button"
        tabIndex={0}
        style={{ cursor: 'pointer' }}
        onClick={onOpen}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen(); }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="name" style={state === 'voided' ? { color: 'var(--text-3)' } : undefined}>
            {loan.worker_name}
          </div>
          <div className="sub">
            {formatRs(loan.amount_rs)}{' '}
            {loan.issued_at ? `given ${formatDate(loan.given_on)}` : `asked for ${formatDate(loan.given_on)}`}
            {' · '}{formatRs(loan.weekly_rs)} {perPeriod(loan.repay_cycle)}{' · '}{loan.reason}
          </div>
        </div>
        <div className="stat" style={{ textAlign: 'right' }}>
          <div className="name">
            {state === 'voided' ? '—' : state === 'paid' ? formatRs(0) : `${formatRs(loan.balance_rs)} left`}
          </div>
          <span className={`pill ${pill.cls}`}>{pill.label}</span>
        </div>
      </div>
      {open && <LoanDetail loan={loan} isOwner={isOwner} onChanged={onChanged} onSign={onSign} />}
    </>
  );
}

function LoanDetail({
  loan, isOwner, onChanged, onSign,
}: {
  loan: LoanRow;
  isOwner: boolean;
  onChanged: () => Promise<void>;
  onSign: () => void;
}) {
  const state = loanState(loan);
  const [repayments, setRepayments] = useState<LoanRepayment[] | null>(null);
  const [weekly, setWeekly] = useState(String(loan.weekly_rs));
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidTried, setVoidTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => setWeekly(String(loan.weekly_rs)), [loan.weekly_rs]);
  useEffect(() => {
    let live = true;
    loanApi.get(loan.id)
      .then((result) => { if (live) setRepayments(result.repayments); })
      .catch((err: unknown) => {
        if (live) setError(err instanceof ApiError ? err.message : 'Could not load its record.');
      });
    return () => { live = false; };
  }, [loan.id, loan.row_version]);

  // Who may do what. The Worker checks all of it again.
  const canRepace = state !== 'voided' && state !== 'paid' && (isOwner || state === 'waiting');
  const canApprove = isOwner && state === 'waiting';
  const canVoid = state !== 'voided' && loan.repaid_rs === 0 && (isOwner || state === 'waiting');

  const weeklyRs = Math.trunc(Number(weekly) || 0);
  const weeklyBad = weeklyRs <= 0 || weeklyRs > loan.amount_rs;

  async function act(work: () => Promise<unknown>) {
    setBusy(true);
    setError('');
    try {
      await work();
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        background: 'var(--surface-2)', borderRadius: 12,
        padding: '10px 12px', margin: '0 -4px 8px',
      }}
    >
      {state !== 'voided' && (
        <div className="grid2" style={{ marginBottom: 10 }}>
          <div className="metric" style={{ background: 'var(--surface)' }}>
            <div className="k">Repaid</div>
            <div className="v">{formatRs(loan.repaid_rs)}</div>
            <div className="k">of {formatRs(loan.amount_rs)}</div>
          </div>
          <div className="metric" style={{ background: 'var(--surface)' }}>
            <div className="k">Still owed</div>
            <div className="v">{formatRs(loan.balance_rs)}</div>
            <div className="k">
              {loan.balance_rs > 0
                ? `about ${weeksToRepay(loan.balance_rs, loan.weekly_rs)} more ${periods(loan.repay_cycle, weeksToRepay(loan.balance_rs, loan.weekly_rs))}`
                : 'paid off'}
            </div>
          </div>
        </div>
      )}

      <div className="sub" style={{ marginBottom: 4 }}>Repayments from pay runs</div>
      {!repayments && <div className="empty">Loading…</div>}
      {repayments && repayments.length === 0 && (
        <div className="sub" style={{ marginBottom: 8 }}>
          {state === 'waiting'
            ? 'Nothing comes off until the owner approves it and the cash is handed over.'
            : state === 'toIssue'
              ? 'Nothing comes off until the cash is handed over and he has signed for it.'
              : state === 'voided'
              ? 'Nothing was repaid.'
                : 'Nothing repaid yet. It comes off in the next pay run he is in.'}
        </div>
      )}
      {repayments && repayments.length > 0 && (
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Pay run</th>
                <th className="num">Taken</th>
                <th className="num">Still owed after</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {repayments.map((row) => (
                <tr key={row.pay_run_id}>
                  <td>{formatDate(row.period_from)} – {formatDate(row.period_to)}</td>
                  <td className="num">{formatRs(row.amount_rs)}</td>
                  <td className="num">{formatRs(row.balance_after_rs)}</td>
                  <td>
                    {row.status === 'approved'
                      ? <span className="pill p-ok">Approved</span>
                      : <span className="pill p-wait">Unapproved</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canRepace && (
        <div className="fld" style={{ marginTop: 8 }}>
          <div>
            {loan.repay_cycle === 'monthly' ? 'Monthly repayment' : 'Weekly repayment'}
            <div className="sub">
              {weeklyBad
                ? <span style={{ color: 'var(--text-danger)' }}>Above zero, and no more than the loan</span>
                : `About ${weeksToRepay(loan.balance_rs, weeklyRs)} ${loan.repay_cycle === 'monthly' ? 'months' : 'full weeks'} from now at this amount`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="number" min="1" step="1" inputMode="numeric"
              value={weekly} onChange={(e) => setWeekly(e.target.value)}
              style={{ width: 120, ...(weeklyBad ? { borderColor: 'var(--danger)' } : {}) }}
            />
            <button
              type="button"
              className="btn sm"
              disabled={busy || weeklyBad || weeklyRs === loan.weekly_rs}
              onClick={() => void act(() => loanApi.update(loan.id, loan.row_version, { weeklyRs }))}
            >
              Save
            </button>
          </div>
        </div>
      )}

      <div className="sub" style={{ marginTop: 8 }}>
        Asked for by {loan.created_by_name}
        {loan.approved_at && loan.approved_by_name && ` · approved by ${loan.approved_by_name}`}
        {loan.issued_at && loan.issued_by_name && ` · handed over by ${loan.issued_by_name} on ${formatDate(loan.given_on)}`}
        {loan.voided_at && ` · voided: ${loan.void_reason ?? ''}`}
      </div>

      {state === 'waiting' && (
        <div className="sub" style={{ marginTop: 6 }}>
          Waiting for the owner to approve. The cash is handed over after that.
        </div>
      )}
      {state === 'toIssue' && (
        <div className="fld" style={{ marginTop: 6 }}>
          <div>
            Hand over the cash
            <div className="sub">Approved. Give him {formatRs(loan.amount_rs)} and have him sign for it.</div>
          </div>
          <button type="button" className="btn sm pri" onClick={onSign}>
            Issue money
          </button>
        </div>
      )}
      {(state === 'repaying' || state === 'paid') && (
        <div className="fld" style={{ marginTop: 6 }}>
          <div>
            Receipt
            <div className="sub">
              Signed for on the tablet {loan.signed_at ? new Date(loan.signed_at).toLocaleString('en-GB') : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="btn sm" onClick={onSign}>
              Signature
            </button>
            <button
              type="button"
              className="btn sm"
              onClick={() => openPrint(`Loan receipt — ${loan.worker_name}`, <LoanReceipt loan={loan} />)}
            >
              Print receipt
            </button>
          </div>
        </div>
      )}

      {error && <div className="banner b-no">{error}</div>}

      {(canApprove || canVoid) && (
        <div className="bar" style={{ marginTop: 10, flexWrap: 'wrap' }}>
          {canApprove && (
            <button
              type="button"
              className="btn sm pri"
              disabled={busy}
              onClick={() => void act(() => loanApi.approve(loan.id))}
            >
              Approve loan
            </button>
          )}
          {/* No confirm() — an inline confirmation with the reason it needs. */}
          {canVoid && !voiding && (
            <button type="button" className="btn sm dang" onClick={() => setVoiding(true)}>
              Void
            </button>
          )}
        </div>
      )}
      {voiding && (
        <div style={{ marginTop: 8 }}>
          <div className="sub" style={{ marginBottom: 4 }}>
            Why is it being voided?
            {voidTried && voidReason.trim() === '' && (
              <span style={{ color: 'var(--text-danger)' }}> — write a reason</span>
            )}
          </div>
          <input
            type="text"
            value={voidReason}
            placeholder="Entered twice, wrong man…"
            onChange={(e) => setVoidReason(e.target.value)}
            style={{
              width: '100%', textAlign: 'left',
              ...(voidTried && voidReason.trim() === '' ? { borderColor: 'var(--danger)' } : {}),
            }}
          />
          <div className="bar" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="btn sm dang"
              disabled={busy}
              onClick={() => {
                setVoidTried(true);
                if (voidReason.trim() === '') return;
                void act(() => loanApi.void(loan.id, voidReason.trim()));
              }}
            >
              Yes, void it
            </button>
            <button type="button" className="btn sm" onClick={() => { setVoiding(false); setVoidTried(false); }}>
              Keep it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------- the signature

/**
 * "Loan received — signature": handing over the cash. His first signature
 * issues the loan (it repays from this week); signing again only replaces the
 * image. Save is disabled until there is ink; Clear wipes it; Not now leaves
 * the loan approved but not handed over.
 */
function SignLoan({
  loan, onClose, onSaved,
}: {
  loan: LoanRow;
  onClose: () => void;
  onSaved: (loan: LoanRow) => Promise<void>;
}) {
  const padRef = useRef<InkPadHandle | null>(null);
  const [hasInk, setHasInk] = useState(false);
  const [resigning, setResigning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const showSigned = !!loan.signed && !resigning;

  async function save() {
    setSaving(true);
    setError('');
    try {
      if (!padRef.current) throw new Error('There is no signature to save.');
      const png = await padRef.current.toPng();
      const { loan: saved } = await loanApi.saveSignature(loan.id, png);
      setResigning(false);
      await onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the signature.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Loan received — signature">
      <div className="mbox">
        <div className="mhead">Loan received — signature</div>
        <div className="sub" style={{ marginBottom: 12 }}>
          {loan.issued_at
            ? 'His signature for the cash he was handed.'
            : 'Hand over the cash and have him sign on the tablet. Signing records the loan as given — repayments start from this week.'}
        </div>

        <div className="card" style={{ margin: '0 0 10px', background: 'var(--surface-2)' }}>
          <div className="row" style={{ paddingTop: 0 }}>
            <div>
              <div className="name">{loan.worker_name}</div>
              <div className="sub">{loan.reason}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="name" style={{ fontSize: 22 }}>{formatRs(loan.amount_rs)}</div>
              <div className="sub">{loan.issued_at ? formatDate(loan.given_on) : 'today'}</div>
            </div>
          </div>
          <div className="sub" style={{ marginTop: 2 }}>
            Rupees {rupeesInWords(loan.amount_rs)} only · repaid {formatRs(loan.weekly_rs)} {perPeriod(loan.repay_cycle)}
            from his pay
          </div>
        </div>

        {showSigned ? (
          <>
            <div className="sub">
              Signed on {loan.signed_at ? new Date(loan.signed_at).toLocaleString('en-GB') : 'the tablet'}
            </div>
            {/* Fetched with the session token — an <img src> would come back 401. */}
            <div className="sigdone">
              <SignatureImage path={loanApi.signaturePath(loan.id, loan.signed_at)} />
            </div>
            <div className="bar" style={{ marginTop: 12 }}>
              <button type="button" className="btn" onClick={onClose}>Close</button>
              <button type="button" className="btn" onClick={() => setResigning(true)}>Sign again</button>
              <button
                type="button"
                className="btn pri"
                onClick={() => {
                  openPrint(`Loan receipt — ${loan.worker_name}`, <LoanReceipt loan={loan} />);
                  onClose();
                }}
              >
                Print receipt
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="sub">Signature of {loan.worker_name}</div>
            <InkPad ref={padRef} onInk={setHasInk} hasInk={hasInk} />
            {error && <div className="err">{error}</div>}
            <div className="bar" style={{ marginTop: 10 }}>
              <button type="button" className="btn" onClick={() => padRef.current?.clear()} disabled={saving}>
                Clear
              </button>
              <button type="button" className="btn" onClick={onClose} disabled={saving}>
                Not now
              </button>
              <button
                type="button"
                className="btn pri"
                disabled={!hasInk || saving}
                onClick={() => void save()}
              >
                {saving ? 'Saving…' : 'Save signature'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
